import useTranslation from "next-translate/useTranslation";
import Popup from "../../../components/ui/popup";
import { Button } from "@/components/ui/button";
import { ChessSets } from "@chesshotel/constants";
import { updateSetting } from "@/redux/settings";
import { useState } from "react";
import { ChessSetPreview } from "../../settings/ChessSet";

const getPieceUrl = (chessSet: string, piece: string) =>
  `/images/pieces/${chessSet}/${piece}.svg`;

interface ChessSetPreviewContainerProps {
  chessSet: string;
  label: string;
  lightColor: string;
  darkColor: string;
  isSelected: boolean;
  onSelect: () => void;
}

function ChessSetPreviewContainer({
  chessSet,
  label,
  lightColor,
  darkColor,
  isSelected,
  onSelect,
}: ChessSetPreviewContainerProps) {
  return (
    <button
      onClick={onSelect}
      className={`relative flex flex-col items-center p-4 rounded-xl transition-all cursor-pointer border-3 ${
        isSelected
          ? "bg-amber-50 border-amber-500 ring-2 ring-amber-200 shadow-md"
          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      }`}
    >
      <ChessSetPreview
        chessSet={chessSet}
        lightColor={lightColor}
        darkColor={darkColor}
        rows={2}
      />

      <span
        className={`font-semibold mt-3 text-base ${
          isSelected ? "text-amber-700" : "text-gray-700"
        }`}
      >
        {label}
      </span>
      {isSelected && (
        <div className="flex absolute -top-2 -right-2 justify-center items-center w-7 h-7 bg-amber-500 rounded-full shadow-md">
          <svg
            className="w-5 h-5 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
      )}
    </button>
  );
}

const CHESS_SET_OPTIONS = [
  {
    id: ChessSets.CHESSHOTEL,
    label: "Classic",
    lightColor: "#f6f6f6",
    darkColor: "#ac9c8d",
  },
  {
    id: ChessSets.CHESSHOTEL_NEW,
    label: "Modern",
    lightColor: "#EDD6B0",
    darkColor: "#B98761",
  },
];

interface ChessSetPopupProps {
  onClose: () => void;
}

export default function ChessSetPopup({ onClose }: ChessSetPopupProps) {
  const { t } = useTranslation("common");
  const { t: lobbyT } = useTranslation("lobby");
  const [selected, setSelected] = useState<string>(ChessSets.CHESSHOTEL_NEW);

  function handleConfirm() {
    updateSetting("chessSet", selected);
    localStorage.setItem("chessSetPopupSeen", "true");
    onClose();
  }

  function handleClose() {
    localStorage.setItem("chessSetPopupSeen", "true");
    onClose();
  }

  return (
    <Popup
      title={lobbyT("Choose your chess pieces")}
      subtitle={lobbyT("We have a new piece set! Pick your favorite style")}
      onClose={handleClose}
      maxWidth="780px"
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-6 items-center sm:flex-row sm:justify-center">
          {CHESS_SET_OPTIONS.map((option) => (
            <ChessSetPreviewContainer
              key={option.id}
              chessSet={option.id}
              label={option.label}
              lightColor={option.lightColor}
              darkColor={option.darkColor}
              isSelected={selected === option.id}
              onSelect={() => setSelected(option.id)}
            />
          ))}
        </div>
        <div className="flex justify-center pt-4">
          <Button variant="default" size="wide" onClick={handleConfirm}>
            {t("Confirm")}
          </Button>
        </div>
      </div>
    </Popup>
  );
}
